import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuditorComplianceDashboard } from '@/api/hooks/useAuditorAccess';
import { useAuditorAuthStore } from '@/store/auditor-auth.store';

function ReadinessBar({ pct }: { pct: number }) {
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-400' : 'bg-red-400';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-bg-surface-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function AuditorDashboardPage() {
  const { session } = useAuditorAuthStore();
  const { data, isLoading } = useAuditorComplianceDashboard();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">
          Welcome, {session?.auditorDisplayName}
        </h1>
        <p className="text-sm text-text-secondary">
          {session?.auditFirmName} · Read-only access expires{' '}
          {session ? new Date(session.expiresAt).toLocaleDateString() : ''}
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-text-tertiary">Frameworks in scope</p>
                <p className="text-3xl font-bold text-text-primary">{data?.totalFrameworks ?? 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-text-tertiary">Avg. Readiness</p>
                <p className="text-3xl font-bold text-text-primary">
                  {data?.averageReadinessPct != null ? `${data.averageReadinessPct}%` : '—'}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <BarChart3 className="h-4 w-4" /> Framework Readiness
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data?.frameworks.map((fw) => (
                <div key={fw.id}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <Link
                      to={`/auditor/frameworks/${fw.id}`}
                      className="font-medium text-text-primary hover:underline flex items-center gap-1"
                    >
                      {fw.displayName}
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                    <span className="text-text-secondary">
                      {fw.readinessPct != null ? `${fw.readinessPct}%` : '—'}
                    </span>
                  </div>
                  <ReadinessBar pct={fw.readinessPct ?? 0} />
                  <p className="mt-0.5 text-xs text-text-tertiary">
                    {fw.implementedCount ?? 0} / {fw.totalControls} controls implemented
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
